<template>
  <Cobrand :affiliates="true">
    <LazySignModal v-if="showLegalModal" />
    <TokenExpiryModal></TokenExpiryModal>
    <MarketplaceNav :showAppConfigNav="true" />
    <div class="marketplace-body-footer-container d-flex flex-column vh-100 overflow-x-hidden"
         data-smooth-scroll="main"
         :class="{'customized-affiliate': !!affiliateCustomizations}"
         :style="appStyleProps">
      <div class="d-flex flex-lg-grow-1 align-items-center align-items-lg-start flex-column mb-auto docs-container">
        <div class="px-3 px-lg-0 py-lg-0 marketplace-page" :class="{'customized-affiliate': !!affiliateCustomizations}">
          <AppConfigMobileNav />
          <slot />
        </div>
      </div>
      <div class="d-flex align-content-between flex-column mt-auto">
        <MarketplaceFooter class="mb-3" />
      </div>
    </div>
    <ChatBot v-if="authUser" />
  </Cobrand>
</template>

<script>
import { LazySignModal } from '#components'
import { mapState } from 'pinia'
import { useAffiliateCustomizationStore } from '~/store/affiliate-customization';
import { useBorrowerStore } from '~/store/borrower';
import { useProgressStore } from '~/store/progress'
import { useRootStore } from '~/store/root'
import { useUserStore } from '~/store/user'
import ChatBot from '~/components/ChatBot.vue'
import Cobrand from '~/components/Cobrand.vue'
import MarketplaceFooter from '~/components/marketplace/MarketplaceFooter.vue'
import MarketplaceNav from '~/components/marketplace/MarketplaceNav.vue'
import SmoothScroll from '~/libs/smooth-scroll'
import TokenExpiryModal from '~/components/TokenExpiryModal.vue'
import { useApplicationConfigStore } from '~/store/application-config'
import AppConfigMobileNav from "~/components/app-config/AppConfigMobileNav.vue";

export default {
  components: {
    AppConfigMobileNav,
    MarketplaceFooter,
    MarketplaceNav,
    ChatBot,
    Cobrand,
    LazySignModal,
    TokenExpiryModal
  },
  watch: {
    '$route.path' () {
      SmoothScroll.goTo(0)
      SmoothScroll.scrollElementsTop('[data-smooth-scroll="main"]')
    },
  },
  async setup () {
    const rootStore = useRootStore()
    const affiliateCustomizationsStore = useAffiliateCustomizationStore()
    const appConfigStore = useApplicationConfigStore()
    const userStore = useUserStore()
    const borrowerStore = useBorrowerStore()
    const { showLegalModal } = storeToRefs(borrowerStore)
    rootStore.setPageTitle(affiliateCustomizationsStore.headTitlePrefix + ' - Marketplace Application')
    rootStore.setLendioFavicons()

    useHead({
      title: rootStore.pageTitle,
      link: rootStore.favicons,
    })

    return {
      showLegalModal,
      rootStore,
      userStore,
      progressStore: useProgressStore(),
      appConfigStore,
    }
  },

  computed: {
    ...mapState(useRootStore, ['authUser', 'canUseEmbedded']),
    ...mapState(useAffiliateCustomizationStore, [
      'appStyleProps',
      'affiliateCustomizations',
    ]),
  },
  methods: {
    logout () {
      this.rootStore.logout({ $router: this.$router, path: this.$route && this.$route.path })

      if (process.client && window) {
        window.logout()
      }
    },
  },
  async beforeMount(){
    const isCypressUser = this.rootStore.isCypressUser
    if (isCypressUser) {
      return
    }

    const appConfig = this.appConfigStore.applicationConfig

    if (appConfig === null){
      const getByIdentifierAndSlug = this.appConfigStore.getByIdentifierAndSlug
      await getByIdentifierAndSlug({ identifier: 'marketplace' })
    }
  },
  async mounted() {
    await this.progressStore.fetchProgress()
    await this.userStore.loadFreshLogin()

    if (this.canUseEmbedded && window.lendio && !window.lendio.hasIdentified) {
      const embeddedInitBody = this.rootStore.getEmbeddedInitBody
      if (embeddedInitBody) {
        window.lendio.initialize(embeddedInitBody)
      }
    }
  },
}
</script>

<style lang="scss">
.marketplace-body-footer-container {
  @include media-breakpoint-up(lg) {
    padding-left: 400px;
  }
  &.customized-affiliate {
    background: $app-body-background-color;
  }
}

.marketplace-page {
  margin-top: 90px; /* Spaced to match .sidebar-nav in MarketplaceSidebar minus back btn and 16 margin */
  width: 100%;

  .no-value-select {
    color: $type-400;
  }

  &.customized-affiliate {
    @include affiliateCustomizedApp();
  }

  @include media-breakpoint-up(sm) {
    width: 90%;
  }

  @include media-breakpoint-up(md) {
    width: 100%;
    max-width: 500px;
  }
}


.no-back-button-padding {
  padding-top: 58px; /* When a back button is included it takes up 58px */
}

.marketplace-header {
  :not(h3) {
    color: $type-500
  }
}

.marketplace-content {
  > * {
    margin-bottom: 24px;
  }
}

.marketplace-content > :last-child {
  margin-bottom: 32px !important;
}

.marketplace-content > *:not(.marketplace-content-wide) {
  max-width: 400px;
}

.marketplace-content-wide {
  @include media-breakpoint-up(xl) {
    width: 600px
  }
}

.docs-container {
  @include media-breakpoint-down(xl) {
    padding-right: 68px;
  }
  @include media-breakpoint-down(lg) {
    padding-right: 0px;
  }
}
</style>
